import React from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

/**
 * React Router v6 compatibility shim for class components that rely on the
 * v3 router/params/location props injected by withRouter.
 *
 * Usage (same call-site as before):
 *   export default withRouter(connect(...)(MyComponent))
 *
 * Provides:
 *   this.props.router.push(path)
 *   this.props.router.replace(path)
 *   this.props.params.*
 *   this.props.location.pathname / .search / .state
 */
export function withRouter(WrappedComponent) {
  function WithRouterWrapper(props) {
    const navigate = useNavigate()
    const params = useParams()
    const rawLocation = useLocation()

    // React Router v3 compat shim: add location.query so legacy components
    // using location.query.page / location.query.xxx continue to work.
    const location = Object.assign({}, rawLocation, {
      query: Object.fromEntries(new URLSearchParams(rawLocation.search)),
    })

    const router = {
      /** Accepts either a string path or a v3-style {pathname, query} object. */
      push: (to) => {
        if (to && typeof to === 'object' && to.query) {
          const qs = new URLSearchParams(to.query).toString()
          navigate({ pathname: to.pathname, search: qs ? `?${qs}` : '' })
        } else {
          navigate(to)
        }
      },
      replace: (to) => {
        if (to && typeof to === 'object' && to.query) {
          const qs = new URLSearchParams(to.query).toString()
          navigate({ pathname: to.pathname, search: qs ? `?${qs}` : '' }, { replace: true })
        } else {
          navigate(to, { replace: true })
        }
      },
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
    }

    return (
      <WrappedComponent
        {...props}
        router={router}
        params={params}
        routeParams={params}
        location={location}
      />
    )
  }

  WithRouterWrapper.displayName = `withRouter(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`
  return WithRouterWrapper
}

export default withRouter
